import type { PricingProfileConfig } from '@techgitter/contracts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CACHE_KEY = 'pricing_profiles_cache_v1';
const TTL_MS = 86_400_000; // 24 hours
const SCHEMA_VERSION = 1;
const API_URL = '/api/pricing-profiles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CacheEnvelope {
  cachedAt: number;       // Unix ms
  ttlMs: number;
  schemaVersion: number;
  config: PricingProfileConfig;
}

export interface GetProfilesConfigOptions {
  /** Bypass cache and fetch from network */
  forceRefresh?: boolean;
  /** If true, return stale cache even when a refresh is attempted */
  allowStale?: boolean;
}

export interface GetProfilesConfigResult {
  config: PricingProfileConfig;
  isStale?: boolean;
  warning?: string;
}

export interface RefreshResult {
  success: boolean;
  config?: PricingProfileConfig;
  error?: string;
}

// ---------------------------------------------------------------------------
// Module-level state (in-memory tier)
// ---------------------------------------------------------------------------

let memoryCache: CacheEnvelope | null = null;
let inflightRequest: Promise<GetProfilesConfigResult> | null = null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isFresh(envelope: CacheEnvelope): boolean {
  return Date.now() - envelope.cachedAt < envelope.ttlMs;
}

function readLocalStorage(): CacheEnvelope | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CacheEnvelope = JSON.parse(raw);
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalStorage(envelope: CacheEnvelope): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // localStorage full or unavailable – silent fallback to memory-only
  }
}

function buildEnvelope(config: PricingProfileConfig): CacheEnvelope {
  return {
    cachedAt: Date.now(),
    ttlMs: TTL_MS,
    schemaVersion: SCHEMA_VERSION,
    config,
  };
}

function storeEnvelope(envelope: CacheEnvelope): void {
  memoryCache = envelope;
  writeLocalStorage(envelope);
}

/** Resolve the best available cached envelope (memory → localStorage). */
function resolveCache(): CacheEnvelope | null {
  if (memoryCache) return memoryCache;
  const stored = readLocalStorage();
  if (stored) {
    memoryCache = stored; // promote to memory
  }
  return stored;
}

async function fetchFromNetwork(): Promise<PricingProfileConfig> {
  const response = await fetch(API_URL);
  if (!response.ok) {
    throw new Error(`Pricing profiles fetch failed: ${response.status}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch the pricing profiles config using two-tier cache with stale fallback.
 *
 * Policy:
 * - Fresh cache → return immediately (no network)
 * - Expired cache → try network; on failure return stale with warning
 * - No cache → fetch from network; throw if network also fails
 *
 * Concurrent calls share a single in-flight request.
 */
export async function getProfilesConfig(
  options?: GetProfilesConfigOptions,
): Promise<GetProfilesConfigResult> {
  const { forceRefresh = false, allowStale = true } = options ?? {};

  // --- Fast path: fresh in-memory / localStorage cache ---
  if (!forceRefresh) {
    const cached = resolveCache();
    if (cached && isFresh(cached)) {
      return { config: cached.config };
    }
  }

  // --- Deduplicate concurrent network requests ---
  if (inflightRequest && !forceRefresh) {
    return inflightRequest;
  }

  const networkPromise = (async (): Promise<GetProfilesConfigResult> => {
    try {
      const config = await fetchFromNetwork();
      const envelope = buildEnvelope(config);
      storeEnvelope(envelope);
      return { config };
    } catch (err: any) {
      // Stale fallback
      if (allowStale) {
        const stale = resolveCache();
        if (stale) {
          return {
            config: stale.config,
            isStale: true,
            warning: `Using cached pricing profiles (network error: ${err.message})`,
          };
        }
      }
      throw err;
    } finally {
      inflightRequest = null;
    }
  })();

  inflightRequest = networkPromise;
  return networkPromise;
}

/**
 * Force-refresh the cache. Designed for a Settings "Refresh" button.
 * On failure the existing cache is preserved.
 */
export async function refreshProfilesConfigCache(): Promise<RefreshResult> {
  try {
    const config = await fetchFromNetwork();
    const envelope = buildEnvelope(config);
    storeEnvelope(envelope);
    return { success: true, config };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Wipe both memory and localStorage caches.
 */
export function invalidateProfilesConfigCache(): void {
  memoryCache = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Cache metadata (used by DebugPanel)
// ---------------------------------------------------------------------------

export function getProfilesConfigCacheMetadata(): {
  cachedAt: number;
  ttlMs: number;
  configVersion: string;
} | null {
  const envelope = resolveCache();
  if (!envelope) return null;
  return {
    cachedAt: envelope.cachedAt,
    ttlMs: envelope.ttlMs,
    configVersion: envelope.config.configVersion,
  };
}

// ---------------------------------------------------------------------------
// Test helpers (exported for unit tests only)
// ---------------------------------------------------------------------------

/** @internal Reset all module state for tests (memory + localStorage). */
export function _resetForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/** @internal Clear only the in-memory cache (simulates page reload). */
export function _clearMemoryForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
}
